import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { DataContext } from "../context/DataContext";
import { getTimezoneAbbr } from "../lib/timezone";
import { formatUSD } from "@/utils/formatters";
import { LeadTimelineResult, LeadTimelineEvent, LeadCompareIdentity } from "../types";
import { useConversationResync } from "../hooks/useConversationResync";
import { useGhlLocationId } from "../hooks/useGhlLocationId";
import { CopyConversationButton } from "./dm/CopyConversationButton";
import { serializeConversation } from "../utils/conversationToText";
import { useRBAC } from "../hooks/useRBAC";
import RecordingPlayer from "./ui/RecordingPlayer";
import DialReviewModal from "./DialReviewModal";
import { parseWordTimes } from "../hooks/useDialProof";
import type { ReviewCall } from "./dial-analytics/callReviewParts";
import { useMemberRoles } from "../hooks/useMemberRoles";
import { TranscriptPanel } from "./LeadTimelineModal/TranscriptPanel";
import Tooltip from "./ui/Tooltip";
import { useGhlUserNameMap } from "../hooks/useGhlUserNameMap";
import { getOwnerInitials, getOwnerColor } from "../utils/ownerBadge";

// =============================================================================
// LEAD TIMELINE PANEL
// =============================================================================
// Extracted from LeadTimelineModal. Owns ALL state, data-fetching, and render
// logic. LeadTimelineModal is now a thin portal/shell wrapper around this.
//
// KEY DIFFERENCE FROM THE MODAL: this component stays mounted while the host
// changes `email` (e.g. navigating between leads in an inline table). A
// dedicated reset effect keyed on `email` clears all stale state before the
// next fetch fires.
// =============================================================================

export interface PriorDialerInfo {
  dialerId: string;
  name: string;
  attempts: number;
  lastAt: string;
}

export interface LeadTimelinePanelProps {
  /** Lead email. Pass null (with anchorRecordingId) for orphan/transcript-only mode. */
  email: string | null;
  /** When absent, the X close button is NOT rendered. */
  onClose?: () => void;
  /** Highlight a specific message in the DM panel (e.g., Calendly link) */
  highlightTimestamp?: string;
  /** Required when anchorRecordingId is set; optional otherwise */
  organizationId?: string;
  /** Open the Transcript tab pre-loaded with this recording */
  anchorRecordingId?: number;
  /** Scroll to and highlight this turn index within the transcript */
  anchorTurnIndex?: number;
  /** Org timezone for TranscriptPanel date display; defaults to 'UTC' */
  orgTz?: string;
  /** F5: Prior dialer info for the selected DialHQ lead — surfaced at the top of the timeline. */
  priorDialer?: PriorDialerInfo;
  /**
   * Where dial rows render. "inline" (default) merges them chronologically into
   * the left Timeline column — the original layout, kept for narrow hosts like
   * LostLeadsPage's side pane. "column" pulls dials into their own third column
   * (Timeline | DMs | Dials) — used by LeadTimelineModal (Mako, 2026-07-02).
   */
  dialsLayout?: "inline" | "column";
  /**
   * Pool-only (Mako 2026-07-10): fold the DMs + Dials rails to color-coded strips BY DEFAULT
   * even when they hold data (green = has history, grey = empty), so the pane is a
   * timeline-focused triage glance. Default false = legacy "fold only when empty".
   */
  railsCollapsedByDefault?: boolean;
  /**
   * Combined-header slots (LostLeadsPage triage, Mako 2026-07-11). When EITHER is set
   * the panel renders a COMPACT STACKED header instead of the legacy single row, so the
   * host doesn't have to stack a second header above the panel:
   *   headerBadge   — chip rendered directly UNDER the name (the triage type·outcome tag).
   *   headerActions — right-pinned content (days-dead, local time, Self-Set CTA).
   * In this mode contacts collapse to click-to-copy icon chips and the grey status badge
   * is dropped (the Last-Interaction banner conveys it). Defaults undefined → legacy
   * single-row header, so all other call sites are byte-identical.
   */
  headerBadge?: React.ReactNode;
  headerActions?: React.ReactNode;
  /**
   * Ask D (LostLeadsPage pool, Mako 2026-09-08): host-supplied content rendered
   * directly UNDER the header and ABOVE the call-summary banner. The pool passes a
   * read-only teammate-notes strip here (PoolNotesStrip). The panel stays agnostic
   * about the data — same slot idiom as headerBadge/headerActions. Defaults
   * undefined → renders nothing, so all other non-pool call sites are byte-identical.
   */
  notesSlot?: React.ReactNode;
  /**
   * Compare mode (lead-phone-normalization Step 6). When set, the suggested-match
   * banner names become buttons; clicking one calls this with that match. Undefined
   * (the modal sites + LostLeadsPage) → banner is display-only, byte-identical.
   */
  onOpenCompare?: (match: SuggestedLeadMatch) => void;
  /**
   * Reports this panel's resolved identity up to the host so the compare header can
   * align both leads. Undefined → not called.
   */
  onIdentityResolved?: (identity: LeadCompareIdentity) => void;
  /** aria-label for the close (X) button; defaults "Close modal". */
  closeLabel?: string;
}

// DM Message interface
interface DMMessage {
  id: string;
  message_text: string | null;
  sent_at: string;
  sender_type: "lead" | "setter" | string;
  has_media?: boolean;
  owner_at_send?: string | null;
}

// Conversation data from ghl_conversations
interface ConversationData {
  id: string;
  ghl_contact_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  channel_type?: string;
  ghl_assigned_user_id?: string | null;
}

// Dial row from dial_dials (GHL call sync) for timeline display.
// status is GHL's call status: pending/completed/answered/busy/no-answer/failed/canceled/voicemail.
// connected = completed|answered. recording_url is a dial-recordings bucket path (Opus).
// transcript is local-whisper output — content-accurate, not verbatim-faithful
// (names/brands mangle on 8kHz phone audio); NULL until the transcription lane runs.
interface DialRow {
  id: string;
  started_at: string;
  direction: string | null;
  status: string | null;
  recording_url: string | null;
  duration_sec: number | null;
  transcript: string | null;
  // funnel_at_dial → the course scenario the expand modal auto-selects.
  // transcript_json → word-times for per-line seek in the expand modal (sparse in
  // prod; NULL renders the flat transcript). Both added 2026-09-17.
  funnel_at_dial: string | null;
  transcript_json: unknown;
  // dialer_id + organization_id → the caller's role → course track in the expand modal (2026-10-03).
  dialer_id: string | null;
  organization_id: string;
}

// Step 5 (lead-phone-normalization plan): other leads in the same org that
// share this lead's canonical phone, from get_suggested_lead_matches RPC.
// POINTER ONLY — read-only, no merge action. See sql/get_suggested_lead_matches.sql.
export interface SuggestedLeadMatch {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
}

const EVENT_LABELS: Record<string, string> = {
  webinar_signup: "Webinar Signup",
  typeform_submission: "Typeform",
  pre_call_submission: "Pre-Call Form",
  sales_pre_call: "Pre-Call Form",
  form_submission: "Form Submission",
  low_ticket_purchase: "Low Ticket Purchase",
  call_booked: "Call Booked",
  call_cancelled: "Call Cancelled",
  sales_call_outcome: "Sales Call",
};

const EVENT_COLORS: Record<string, string> = {
  webinar_signup: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  typeform_submission: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  pre_call_submission: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  sales_pre_call: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  form_submission: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  low_ticket_purchase: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  call_booked: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  call_cancelled: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  sales_call_outcome: "bg-ds-green-bg text-ds-green border-emerald-500/20",
};

const DOT_COLORS: Record<string, string> = {
  webinar_signup: "bg-purple-500",
  typeform_submission: "bg-purple-500",
  pre_call_submission: "bg-purple-500",
  sales_pre_call: "bg-purple-500",
  form_submission: "bg-purple-500",
  low_ticket_purchase: "bg-indigo-500",
  call_booked: "bg-blue-500",
  call_cancelled: "bg-amber-500",
  sales_call_outcome: "bg-emerald-500",
};

function statusBadge(status: string | null): string {
  if (!status) return "bg-black/[0.05] text-glass-secondary";
  const s = status.toLowerCase();
  if (s.includes("paid") || s.includes("closed") || s.includes("sale") || s.includes("deposit"))
    return "bg-ds-green-bg text-ds-green";
  if (s.includes("lost")) return "bg-ds-red/15 text-ds-red";
  if (s.includes("no_show") || s.includes("noshow") || s.includes("no show"))
    return "bg-amber-500/15 text-amber-400";
  if (s.includes("installment")) return "bg-ds-accent/15 text-ds-accent";
  return "bg-black/[0.05] text-glass-secondary";
}

function formatDate(d: string | null, timezone: string): string {
  if (!d) return "--";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  });
}

function formatCurrencyOrEmpty(v: number | null | undefined): string {
  if (v == null || v === 0) return "";
  return formatUSD(v);
}

/** Copy text to clipboard and briefly swap displayed text to confirm */
function copyToClipboard(
  text: string,
  setCopiedField: (field: string | null) => void,
  field: string
) {
  navigator.clipboard.writeText(text).then(() => {
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1200);
  });
}

// =============================================================================
// ContactChips — collapsible click-to-copy contact icons (triage combined header)
// =============================================================================
// Mako 2026-07-11: "the email is collapsible, phone number, ig… until people click
// it then it opens up and gets copied at the same time." Each chip is icon-only
// until clicked; a click COPIES the value AND expands the chip to reveal it. A
// second click collapses it (no re-copy). Tooltip gives the discoverability the
// bare icon can't. Only used by the combined header — the legacy header keeps its
// always-open text spans untouched.
const CONTACT_ICONS: Record<string, React.ReactNode> = {
  email: (
    <svg
      className="w-3.5 h-3.5 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  ),
  phone: (
    <svg
      className="w-3.5 h-3.5 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  ),
  instagram: (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  ),
};

function ContactChips({
  email,
  phone,
  instagram,
  copiedField,
  setCopiedField,
  expanded,
  setExpanded,
}: {
  email: string | null;
  phone: string | null;
  instagram: string | null;
  copiedField: string | null;
  setCopiedField: (field: string | null) => void;
  expanded: Set<string>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const cleanIg = instagram ? instagram.replace(/^@+/, "") : null;
  const items = [
    email ? { field: "email", label: "email", value: email, display: email } : null,
    phone ? { field: "phone", label: "phone number", value: phone, display: phone } : null,
    cleanIg
      ? { field: "instagram", label: "Instagram", value: cleanIg, display: `@${cleanIg}` }
      : null,
  ].filter(Boolean) as { field: string; label: string; value: string; display: string }[];

  if (items.length === 0) return null;

  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {items.map((it) => {
        const isOpen = expanded.has(it.field);
        const isCopied = copiedField === it.field;
        return (
          <Tooltip key={it.field} content={isCopied ? "Copied!" : `Click to copy ${it.label}`}>
            <button
              type="button"
              data-testid={`triage-contact-${it.field}`}
              aria-label={`Copy ${it.label}`}
              onClick={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(it.field)) {
                    next.delete(it.field);
                  } else {
                    next.add(it.field);
                    copyToClipboard(it.value, setCopiedField, it.field);
                  }
                  return next;
                })
              }
              className={[
                "anim-tap inline-flex items-center gap-1 rounded-md px-1.5 py-1 transition-colors",
                "text-glass-muted hover:text-glass-secondary hover:bg-black/[0.04]",
                isOpen || isCopied ? "bg-black/[0.04]" : "",
              ].join(" ")}
            >
              {CONTACT_ICONS[it.field]}
              {(isOpen || isCopied) && (
                <span
                  className={`token-11 whitespace-nowrap ${isCopied ? "text-ds-green font-semibold" : ""}`}
                >
                  {isCopied ? "Copied!" : it.display}
                </span>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

// Format time for DM messages
const formatMessageTime = (dateStr: string, timezone: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  });
};

// Chat bubble component for DM messages
const ChatBubble = React.forwardRef<
  HTMLDivElement,
  { message: DMMessage; timezone: string; highlighted?: boolean; ownerName?: string | null }
>(({ message, timezone, highlighted, ownerName }, ref) => {
  const isLead = message.sender_type === "lead";
  const ringClass = highlighted ? "ring-2 ring-amber-400 ring-offset-2 animate-pulse-once" : "";
  return (
    <div
      ref={ref}
      className={`flex items-end gap-1.5 ${isLead ? "justify-start" : "justify-end"} mb-2`}
    >
      <div
        className={`max-w-[85%] px-3 py-2 rounded-2xl ${ringClass} ${
          isLead
            ? "bg-black/[0.05] text-glass rounded-bl-none"
            : "bg-blue-500 text-glass rounded-br-none"
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">
          {message.message_text || (
            <span className="italic opacity-60">
              {message.has_media
                ? "[Media]"
                : isLead
                  ? "[Story reaction]"
                  : "[ManyChat automation]"}
            </span>
          )}
        </p>
        <p className={`token-10 mt-1 ${isLead ? "text-glass-muted" : "text-blue-100"}`}>
          {formatMessageTime(message.sent_at, timezone)}
          <span className="text-[9px] text-glass-muted ml-0.5">({getTimezoneAbbr(timezone)})</span>
        </p>
      </div>
      {/* owner_at_send badge — who owned the lead when this outbound msg was sent (STEP 0) */}
      {!isLead && ownerName !== undefined && (
        <Tooltip content={ownerName || "Unknown owner"}>
          <span
            data-testid="owner-badge"
            className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center token-9 font-semibold ${getOwnerColor(
              ownerName
            )} ${ownerName ? "text-white" : "text-glass-muted"}`}
          >
            {getOwnerInitials(ownerName)}
          </span>
        </Tooltip>
      )}
    </div>
  );
});
ChatBubble.displayName = "ChatBubble";

// Dial card — one dial_dials row. Shared by both layouts: the inline timeline
// entry wraps it with the rail dot; the Dials column renders it bare.
const DialCard: React.FC<{
  dial: DialRow;
  timezone: string;
  canViewRecordings: boolean;
  leadName?: string | null;
  /** The caller's organization_members.role → course track in the expand modal. */
  dialerRole: string | null;
}> = ({ dial, timezone, canViewRecordings, leadName = null, dialerRole }) => {
  const connected = dial.status === "completed" || dial.status === "answered";
  const [showTranscript, setShowTranscript] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Same RBAC gate as the recording — a transcript IS the recording's content.
  // Rendered independently of recording_url so a transcript survives the 90-day
  // audio cleanup. Whisper filler on near-silence calls ([BLANK_AUDIO], looping
  // "you") renders as-is — mirror faithfully, don't sanitize.
  const hasTranscript = canViewRecordings && Boolean(dial.transcript && dial.transcript.trim());
  // Reviewable = something to open in the expand modal (recording and/or transcript).
  const reviewable = canViewRecordings && (Boolean(dial.recording_url) || hasTranscript);
  // Normalized shape the shared /dialview view (CallReviewDetail) renders.
  const reviewCall: ReviewCall = {
    id: dial.id,
    leadName,
    leadPhone: null,
    funnelAtDial: dial.funnel_at_dial ?? null,
    dialerName: null,
    dialerRole,
    startedAt: dial.started_at,
    durationSec: dial.duration_sec,
    recordingUrl: dial.recording_url,
    words: parseWordTimes(dial.transcript_json),
    flatTranscript: dial.transcript,
  };
  return (
    <div className="bg-black/[0.03] border border-black/[0.04] rounded-lg p-3 hover:border-black/[0.08] transition-colors">
      {/* Top row: badge + date */}
      <div className="flex items-center justify-between mb-2">
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium border ${
            connected
              ? "bg-ds-green-bg text-ds-green border-emerald-500/20"
              : "bg-slate-500/10 text-slate-400 border-slate-500/20"
          }`}
        >
          {dial.direction === "inbound" ? "Inbound Call" : "Dial"}
        </span>
        <span className="text-xs text-glass-muted">
          {formatDateTime(dial.started_at, timezone)}
          <span className="text-[9px] ml-0.5">({getTimezoneAbbr(timezone)})</span>
        </span>
      </div>
      <p className="text-xs text-glass-muted mb-2">
        Status:{" "}
        <span className={`font-medium ${connected ? "text-ds-green" : "text-glass-secondary"}`}>
          {dial.status ? dial.status.replace(/-/g, " ") : "unknown"}
        </span>
        {dial.duration_sec != null && dial.duration_sec > 0 && (
          <span className="ml-1.5">
            · {Math.floor(dial.duration_sec / 60)}:{String(dial.duration_sec % 60).padStart(2, "0")}
          </span>
        )}
      </p>
      {/* Recording player — visible to every in-org role (canViewDialRecordings).
          Server-side gate is the dial_recordings_select RLS policy. Renders nothing
          when the role is unknown/absent rather than a misleading "managers only" note. */}
      {dial.recording_url && canViewRecordings && (
        <RecordingPlayer
          recordingUrl={dial.recording_url}
          occurredAt={dial.started_at}
          durationSec={dial.duration_sec ?? undefined}
        />
      )}
      {reviewable && (
        <div className="mt-2">
          <div className="flex items-center gap-3">
            {hasTranscript && (
              <button
                type="button"
                onClick={() => setShowTranscript((v) => !v)}
                data-testid="dial-transcript-toggle"
                aria-expanded={showTranscript}
                className="text-xs font-medium text-ds-accent hover:underline anim-tap"
              >
                {showTranscript ? "Hide transcript" : "View transcript"}
              </button>
            )}
            {/* Expand → the full /dialview call-review (tall transcript + course). */}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="dial-expand-transcript"
              data-dial-id={dial.id}
              className="text-xs font-medium text-ds-accent hover:underline anim-tap"
            >
              Expand transcript ↗
            </button>
          </div>
          {showTranscript && hasTranscript && (
            <div
              data-testid="dial-transcript"
              className="mt-1.5 max-h-48 overflow-y-auto rounded-lg border border-black/[0.08] bg-black/[0.03] px-3 py-2"
            >
              <p className="text-xs text-glass-secondary whitespace-pre-wrap leading-relaxed">
                {dial.transcript}
              </p>
            </div>
          )}
        </div>
      )}

      <DialReviewModal
        call={expanded ? reviewCall : null}
        isOpen={expanded}
        timezone={timezone}
        onClose={() => setExpanded(false)}
      />
    </div>
  );
};

export const LeadTimelinePanel: React.FC<LeadTimelinePanelProps> = ({
  email,
  onClose,
  highlightTimestamp,
  organizationId,
  anchorRecordingId,
  anchorTurnIndex,
  orgTz,
  priorDialer,
  dialsLayout = "inline",
  railsCollapsedByDefault = false,
  headerBadge,
  headerActions,
  notesSlot,
  onOpenCompare,
  onIdentityResolved,
  closeLabel,
}) => {
  const dataCtx = React.useContext(DataContext);
  // Every in-org role can play dial recordings (Mako ruling 2026-07-15). The old
  // `canManageTeam || isDialer` gate blocked all closers + setters. Server-side
  // enforcement is the dial_recordings_select RLS policy; this flag only shows the
  // player. See useRBAC.canViewDialRecordings for the two-layer note.
  const { canViewDialRecordings } = useRBAC();
  const canViewRecordings = canViewDialRecordings;

  // Orphan mode: no email but a recording to show — left panel hides, right panel fills width
  const isOrphan = !email && Boolean(anchorRecordingId);

  // Third column only renders when a lead exists to hang dials on
  const showDialsColumn = dialsLayout === "column" && !isOrphan;

  const [data, setData] = useState<LeadTimelineResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab control — default to 'transcript' when an anchor recording is provided
  const [activeTab, setActiveTab] = useState<"dms" | "transcript">(
    anchorRecordingId ? "transcript" : "dms"
  );

  // Reset active tab when anchorRecordingId changes (e.g., reopening with different recording)
  useEffect(() => {
    async function run() {
      setActiveTab(anchorRecordingId ? "transcript" : "dms");
    }
    void run();
  }, [anchorRecordingId]);

  // DM Conversation state
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [dmLoading, setDmLoading] = useState(false);
  const [dmError, setDmError] = useState<string | null>(null);
  const [resyncSuccess, setResyncSuccess] = useState<string | null>(null);
  const [assignedSetterName, setAssignedSetterName] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  // Which collapsible contact chips are expanded (triage combined-header only).
  const [expandedContacts, setExpandedContacts] = useState<Set<string>>(() => new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const hasScrolledToHighlight = useRef(false);
  const dialsColumnRef = useRef<HTMLDivElement>(null);

  // Dial events with recordings (Path B — separate query, no RPC change)
  const [dialEvents, setDialEvents] = useState<DialRow[]>([]);
  // Caller roles for the dial cards' course track. Use the dials' OWN org
  // (a super-admin may view a lead from another org); host org as fallback.
  const roleOf = useMemberRoles(dialEvents[0]?.organization_id ?? organizationId ?? null);
  const [leadId, setLeadId] = useState<string | null>(null);

  // Step 5: suggested phone-match leads (read-only pointer, see SuggestedMatchBanner below)
  const [suggestedMatches, setSuggestedMatches] = useState<SuggestedLeadMatch[]>([]);

  // Empty-column collapse (Mako 2026-07-03): a lead with no DM conversation / no
  // dials folds that column to a slim greyed strip so the data that DOES exist
  // gets the width. Click-to-expand keeps Resync reachable. Reset per lead in its
  // own effect — the main email reset effect below is load-bearing, left untouched.
  const [dmsExpanded, setDmsExpanded] = useState(false);
  const [dialsExpanded, setDialsExpanded] = useState(false);
  useEffect(() => {
    setDmsExpanded(false);
    setDialsExpanded(false);
  }, [email]);

  // Resync hook for fetching messages from GHL
  const { resync, isResyncing, error: resyncError } = useConversationResync();

  const orgId = dataCtx?.currentOrganization?.id ?? null;
  // GHL user id → display name, for the owner_at_send bubble badge (STEP 0)
  const ghlUserNames = useGhlUserNameMap(orgId);
  const timezone = dataCtx?.currentOrganization?.timezone || "UTC";

  // GHL location ID for the footer contact deep-link (same pattern as ConversationViewModal)
  const { locationId: ghlLocationId } = useGhlLocationId(orgId ?? "");

  // ==========================================================================
  // RESET EFFECT — keyed on email
  // ==========================================================================
  // When email changes (e.g., user clicks a different lead in an inline list)
  // the panel stays mounted, so without this effect ALL prior state (conversation,
  // messages, dialEvents, leadId, assignedSetterName, copiedField, resyncSuccess,
  // and the hasScrolledToHighlight ref) would remain visible while the new lead's
  // data loads. This effect fires BEFORE the fetch effects because React runs
  // effects in declaration order within a component.
  // ==========================================================================
  useEffect(() => {
    setData(null);
    setError(null);
    setConversation(null);
    setMessages([]);
    setDmError(null);
    setResyncSuccess(null);
    setAssignedSetterName(null);
    setDialEvents([]);
    setLeadId(null);
    setCopiedField(null);
    setSuggestedMatches([]);
    hasScrolledToHighlight.current = false;
  }, [email]);

  const fetchTimeline = useCallback(async () => {
    if (!supabase || !email || !orgId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: result, error: rpcError } = await supabase.rpc("get_lead_timeline", {
        p_email: email,
        p_org_id: orgId,
      });
      if (rpcError) throw rpcError;
      setData(result as LeadTimelineResult);
    } catch (err: unknown) {
      logger.error("Failed to fetch lead timeline:", err);
      setError(err instanceof Error ? err.message : "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [email, orgId]);

  // Step 5: fetch suggested phone-match leads. Fails silently (log only) — this
  // is a pointer-only nice-to-have, never worth surfacing an error banner over.
  const fetchSuggestedMatches = useCallback(async () => {
    if (!supabase || !email || !orgId) return;
    try {
      const { data: matches, error: matchError } = await supabase.rpc(
        "get_suggested_lead_matches",
        { p_email: email, p_org_id: orgId }
      );
      if (matchError) throw matchError;
      setSuggestedMatches((matches as SuggestedLeadMatch[]) || []);
    } catch (err: unknown) {
      logger.error("Failed to fetch suggested lead matches:", err);
      setSuggestedMatches([]);
    }
  }, [email, orgId]);

  // Fetch conversation by looking up lead's ghl_contact_id, then falling back to email/instagram match
  const fetchConversation = useCallback(async () => {
    if (!supabase || !email || !orgId) return;
    setDmLoading(true);
    setDmError(null);
    setConversation(null);
    setMessages([]);

    try {
      // Step 1: Get lead's ghl_contact_id + id from leads table
      const { data: leadData } = await supabase
        .from("leads")
        .select("id, ghl_contact_id, instagram")
        .eq("email", email)
        .eq("organization_id", orgId)
        .limit(1)
        .maybeSingle();

      // Store lead UUID so we can fetch dial events
      if (leadData?.id) setLeadId(leadData.id);

      let conversationData: ConversationData | null = null;

      // Helper: pick the best conversation from candidates — prefer instagram channel
      const pickBest = (rows: ConversationData[] | null): ConversationData | null => {
        if (!rows || rows.length === 0) return null;
        // Prefer instagram channel_type (where actual DMs live)
        const instagram = rows.find((r) => r.channel_type === "instagram");
        return instagram || rows[0];
      };

      const selectCols =
        "id, ghl_contact_id, contact_name, lead_instagram, channel_type, ghl_assigned_user_id";

      // Step 2a: If we have ghl_contact_id, find conversation directly
      // Note: leads.ghl_contact_id sometimes holds a conversation ID (from pre-call forms),
      // so try matching against both ghl_contact_id AND ghl_conversation_id
      if (leadData?.ghl_contact_id) {
        const { data: convByContact } = await supabase
          .from("ghl_conversations")
          .select(selectCols)
          .eq("ghl_contact_id", leadData.ghl_contact_id)
          .eq("organization_id", orgId)
          .limit(10);

        const { data: convByConvId } = await supabase
          .from("ghl_conversations")
          .select(selectCols)
          .eq("ghl_conversation_id", leadData.ghl_contact_id)
          .eq("organization_id", orgId)
          .limit(10);

        // Merge both result sets, deduplicate by id
        const allMatches = [...(convByContact || []), ...(convByConvId || [])];
        const seen = new Set<string>();
        const deduped = allMatches.filter((r) => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });

        conversationData = pickBest(deduped as ConversationData[]);
      }

      // Step 2b: Fallback - try matching by email on ghl_conversations
      if (!conversationData) {
        const { data: convByEmail } = await supabase
          .from("ghl_conversations")
          .select(selectCols)
          .eq("lead_email", email)
          .eq("organization_id", orgId)
          .limit(10);

        conversationData = pickBest(convByEmail as ConversationData[] | null);
      }

      // Step 2c: Fallback - try matching by instagram (if lead has one)
      if (!conversationData && leadData?.instagram) {
        const { data: convByIg } = await supabase
          .from("ghl_conversations")
          .select(selectCols)
          .eq("lead_instagram", leadData.instagram)
          .eq("organization_id", orgId)
          .limit(10);

        conversationData = pickBest(convByIg as ConversationData[] | null);
      }

      if (conversationData) {
        setConversation(conversationData);
        // Look up assigned setter name from profiles
        // TODO: migrate to useProfiles() — Phase 7.2 (docs/PLAN_rbac_security_scale_hardening.md)
        if (conversationData.ghl_assigned_user_id) {
          const { data: setterProfile } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("ghl_user_id", conversationData.ghl_assigned_user_id)
            .limit(1)
            .maybeSingle();
          setAssignedSetterName(setterProfile?.full_name || null);
        } else {
          setAssignedSetterName(null);
        }
        // Fetch messages for this conversation
        await fetchMessages(conversationData.id);
      } else {
        setDmError("No Instagram conversation found for this lead");
      }
    } catch (err: unknown) {
      logger.error("Failed to fetch conversation:", err);
      setDmError(err instanceof Error ? err.message : "Failed to load conversation");
    } finally {
      setDmLoading(false);
    }
  }, [email, orgId]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId: string) => {
    if (!supabase || !conversationId) return;

    try {
      const { data, error: queryError } = await supabase
        .from("ghl_messages")
        .select(
          "id, message_text, sent_at, sender_type, has_media, ghl_sender_user_id, sender_name, message_type, owner_at_send"
        )
        .eq("conversation_id", conversationId)
        .not("message_type", "in", "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)")
        .order("sent_at", { ascending: false })
        .limit(200); // Safety cap: most recent 200 messages

      if (queryError) throw queryError;

      // Filter out system messages and reverse to chronological order
      const filtered = (data || [])
        .filter(
          (m) =>
            !m.message_text?.includes("Opportunity created") &&
            !m.message_text?.includes("Opportunity updated") &&
            !m.message_text?.includes("Opportunity deleted")
        )
        .reverse(); // Reverse DESC to ASC for display

      setMessages(filtered as DMMessage[]);
    } catch (err) {
      logger.error("Failed to fetch messages:", err);
    }
  }, []);

  // Fetch dials (GHL call sync) for this lead. dial_dials.lead_id is populated
  // by the sync's linkage pass (contact-id / email / phone match) — leads that
  // can't be resolved to a GHL contact simply have no dial rows here.
  useEffect(() => {
    if (!leadId || !supabase) return;
    supabase
      .from("dial_dials")
      .select(
        "id, started_at, direction, status, recording_url, duration_sec, transcript, funnel_at_dial, transcript_json, dialer_id, organization_id"
      )
      .eq("lead_id", leadId)
      .order("started_at", { ascending: true })
      .limit(200)
      .then(({ data: rows, error: dialErr }) => {
        if (dialErr) {
          logger.error("Failed to fetch dials:", dialErr);
          return;
        }
        setDialEvents((rows as DialRow[]) || []);
      });
  }, [leadId]);

  // Handle resync button click
  const handleResync = async () => {
    if (!conversation?.id) return;
    setResyncSuccess(null);

    const result = await resync(conversation.id);

    if (result.success) {
      await fetchMessages(conversation.id);
      const synced = result.messages_synced || 0;
      setResyncSuccess(synced > 0 ? `Synced ${synced}` : "No new");
      setTimeout(() => setResyncSuccess(null), 3000);
    }
  };

  // Scroll to highlighted message or bottom when messages load
  useEffect(() => {
    if (messages.length > 0) {
      if (highlightTimestamp && highlightRef.current && !hasScrolledToHighlight.current) {
        hasScrolledToHighlight.current = true;
        requestAnimationFrame(() => {
          highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      } else if (!highlightTimestamp && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages, highlightTimestamp]);

  // Dials column reads oldest→latest like the DM chat (Mako 2026-07-14), so jump
  // to the newest dial at the bottom on load/expand. Container-scoped scrollTop —
  // NOT scrollIntoView — so the modal/page scroll is never tugged (the DM panel's
  // scrollIntoView is fine because DMs are the anchor column; dials aren't).
  useEffect(() => {
    if (dialEvents.length === 0) return;
    const el = dialsColumnRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [dialEvents, dialsExpanded]);

  // Fetch data when email changes (reset effect above clears stale state first)
  useEffect(() => {
    if (email) {
      fetchTimeline();
      fetchConversation();
      fetchSuggestedMatches();
    }
  }, [email, fetchTimeline, fetchConversation, fetchSuggestedMatches]);

  const lead = data?.lead;

  // Step 6 (compare): report this panel's resolved identity up to the host so the
  // compare header can align two leads. IG mirrors the header's own logic (the
  // rawIg = lead?.instagram || conversation?.lead_instagram fallback). Do NOT strip
  // the leading @ here — the header strips for display, the compare marker
  // normalizes at compare time (LeadCompareHeader).
  useEffect(() => {
    if (!onIdentityResolved) return;
    onIdentityResolved({
      email,
      name: lead?.name ?? null,
      phone: lead?.phone ?? null,
      instagram: lead?.instagram || conversation?.lead_instagram || null,
    });
  }, [
    onIdentityResolved,
    email,
    lead?.name,
    lead?.phone,
    lead?.instagram,
    conversation?.lead_instagram,
  ]);

  const timeline = data?.timeline || [];

  // Merge dials (GHL call sync) into the timeline sorted by started_at.
  // Every dial becomes an inline entry; recorded ones get a player.
  type MergedEntry =
    | { kind: "timeline"; event: LeadTimelineEvent; key: string }
    | { kind: "dial"; event: DialRow; key: string };

  const mergedTimeline = useMemo<MergedEntry[]>(() => {
    const tEntries: MergedEntry[] = timeline.map((event, i) => ({
      kind: "timeline" as const,
      event,
      key: `t-${i}`,
    }));
    // Column layout owns the dials — keep them out of the timeline rail
    const dEntries: MergedEntry[] =
      dialsLayout === "column"
        ? []
        : dialEvents.map((event) => ({
            kind: "dial" as const,
            event,
            key: `d-${event.id}`,
          }));
    const entryDate = (e: MergedEntry): string | null =>
      e.kind === "timeline" ? e.event.event_date : e.event.started_at;
    return [...tEntries, ...dEntries].sort((a, b) => {
      const aDate = entryDate(a);
      const bDate = entryDate(b);
      if (!aDate) return -1;
      if (!bDate) return 1;
      return new Date(aDate).getTime() - new Date(bDate).getTime();
    });
  }, [timeline, dialEvents, dialsLayout]);

  // Render-only transform: fuse each call_cancelled + call_booked logged within
  // 5 minutes of each other into ONE "Rescheduled (old → new)" beat. A GHL
  // reschedule writes the cancel + the rebook at the same instant, so a flat
  // list shows "Booked (new), Cancelled (old)" and the cancel reads as undoing
  // the call just booked. Fusing them removes that ambiguity. Pairing is by
  // event_date proximity only — no DB/RPC change. A lone cancel with no rebook
  // in the window stays a real "Call Cancelled" card.
  type RenderEntry =
    | MergedEntry
    | { kind: "reschedule"; from: LeadTimelineEvent; to: LeadTimelineEvent; key: string };

  const renderTimeline = useMemo<RenderEntry[]>(() => {
    const FIVE_MIN_MS = 5 * 60 * 1000;
    type TLEntry = Extract<MergedEntry, { kind: "timeline" }>;
    const isCall = (e: MergedEntry, type: string): e is TLEntry =>
      e.kind === "timeline" && e.event.event_type === type;
    const ms = (e: TLEntry) => (e.event.event_date ? new Date(e.event.event_date).getTime() : null);

    const bookings = mergedTimeline.filter((e): e is TLEntry => isCall(e, "call_booked"));
    const cancels = mergedTimeline.filter((e): e is TLEntry => isCall(e, "call_cancelled"));

    // Pair each cancel with the nearest unused booking within 5 min. The pair
    // renders as a reschedule anchored at the booking's position; the cancel
    // half is suppressed (it's represented by the "from" slot of the beat).
    const usedBooking = new Set<string>();
    const consumed = new Set<string>();
    const anchorToCancel = new Map<string, TLEntry>(); // booking key → its paired cancel

    for (const cancel of cancels) {
      const cTime = ms(cancel);
      if (cTime == null) continue;
      let best: TLEntry | null = null;
      let bestDiff = Infinity;
      for (const book of bookings) {
        if (usedBooking.has(book.key)) continue;
        const bTime = ms(book);
        if (bTime == null) continue;
        const diff = Math.abs(bTime - cTime);
        if (diff <= FIVE_MIN_MS && diff < bestDiff) {
          best = book;
          bestDiff = diff;
        }
      }
      if (best) {
        usedBooking.add(best.key);
        consumed.add(best.key);
        consumed.add(cancel.key);
        anchorToCancel.set(best.key, cancel);
      }
    }

    if (consumed.size === 0) return mergedTimeline;

    const out: RenderEntry[] = [];
    for (const e of mergedTimeline) {
      if (e.kind === "timeline" && anchorToCancel.has(e.key)) {
        const cancel = anchorToCancel.get(e.key)!;
        out.push({ kind: "reschedule", from: cancel.event, to: e.event, key: `r-${e.key}` });
      } else if (consumed.has(e.key)) {
        continue; // the cancelled half — folded into its reschedule beat
      } else {
        out.push(e);
      }
    }
    return out;
  }, [mergedTimeline]);

  // Canonical setter resolution — mirrors trg_resolve_setter_attribution exactly.
  // Filter pre-calls to setter_id NOT NULL AND role IN ('setter','dialer'),
  // sort by role priority (setter=1, dialer=2) then event_date DESC NULLS LAST,
  // pick first. Identity is (setter_id, event_date) — the winning row, not just
  // the winning person — so multiple pre-calls from the same setter don't all
  // light up the pill.
  const canonicalSetter = useMemo(() => {
    const preCalls = timeline.filter(
      (e) =>
        e.event_type === "sales_pre_call" &&
        e.setter_id != null &&
        (e.setter_role === "setter" || e.setter_role === "dialer")
    );
    if (preCalls.length === 0) return null;
    const sorted = [...preCalls].sort((a, b) => {
      const aRole = a.setter_role === "setter" ? 1 : 2;
      const bRole = b.setter_role === "setter" ? 1 : 2;
      if (aRole !== bRole) return aRole - bRole;
      if (a.event_date == null && b.event_date == null) return 0;
      if (a.event_date == null) return 1;
      if (b.event_date == null) return -1;
      return b.event_date.localeCompare(a.event_date);
    });
    const winner = sorted[0];
    return {
      id: winner.setter_id!,
      name: winner.setter_name,
      role: winner.setter_role,
      event_date: winner.event_date,
    };
  }, [timeline]);

  // No-data detection for the collapsible columns. DM "no data" = lookup finished
  // and found no conversation (dmError covers not-found AND load-fail) or an empty
  // one. Never collapse while loading, in orphan mode, or when a transcript tab is
  // anchored (the strip would hide it).
  // Rail collapse. DEFAULT (inline call sites): fold a rail ONLY when it has no data, so the
  // data that DOES exist keeps the width. Callers that opt into railsCollapsedByDefault — the
  // POOL detail pane (Mako 2026-07-10) and LeadTimelineModal (Mako 2026-07-17) — start BOTH
  // rails folded even WITH data, so the surface is a timeline-focused triage glance and the
  // rail COLOR is the at-a-glance signal — green = history in here (click to expand), grey =
  // empty. Safe because DM + dial data load eagerly on mount (effects keyed on email/leadId),
  // so these counts are known while collapsed.
  const hasDms = messages.length > 0;
  const hasDials = dialEvents.length > 0;
  const dmsNoData = !dmLoading && (!!dmError || (!!conversation && messages.length === 0));
  const dmsCollapsed =
    !isOrphan && !anchorRecordingId && !dmsExpanded && (railsCollapsedByDefault || dmsNoData);
  const dialsCollapsed =
    showDialsColumn && !dialsExpanded && (railsCollapsedByDefault || (!loading && !hasDials));
  // Orphan + anchored-transcript mounts pin the DM rail open (dmsCollapsed above can never go
  // true there). Offering the header's click-to-collapse + ‹ chevron in those modes would be a
  // dead control, so the affordance follows what can actually fold — not the raw prop.
  const dmsCanCollapse = railsCollapsedByDefault && !isOrphan && !anchorRecordingId;

  // Combined header (LostLeadsPage triage, Mako 2026-07-11): render ONE compact
  // stacked header — name + collapsible contact icons on row 1, the type·outcome
  // badge directly under the name, actions (days-dead · local time · Self-Set)
  // pinned right — instead of the legacy single row, so the host isn't stacking a
  // second header above us. Gated on the slots, so every other call site keeps the
  // legacy header byte-identical. Grey status badge is dropped here (the
  // Last-Interaction banner conveys it).
  const useCombinedHeader = headerBadge != null || headerActions != null;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {useCombinedHeader ? (
        <div
          data-testid="triage-detail-header"
          className="flex-shrink-0 flex items-center gap-x-4 gap-y-1 px-4 py-2.5 border-b border-black/[0.08] min-w-0"
        >
          {/* Left: name + collapsible contact icons (row 1), badge under the name (row 2).
              gap-0.5 keeps the name + tag tight together (Mako 2026-07-11). */}
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <h2
                id="lead-timeline-modal-title"
                className="token-15 font-bold text-glass truncate min-w-0"
              >
                {lead?.name || email || "(no linked lead)"}
              </h2>
              <ContactChips
                email={email}
                phone={lead?.phone ?? null}
                instagram={lead?.instagram || conversation?.lead_instagram || null}
                copiedField={copiedField}
                setCopiedField={setCopiedField}
                expanded={expandedContacts}
                setExpanded={setExpandedContacts}
              />
            </div>
            {headerBadge && <div className="flex items-center min-w-0">{headerBadge}</div>}
          </div>
          {/* Right: actions — days-dead · local time · Self-Set */}
          {headerActions && (
            <div className="ml-auto flex-shrink-0 flex items-center gap-x-3 gap-y-1 flex-wrap justify-end">
              {headerActions}
            </div>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="flex-shrink-0 text-glass-muted hover:text-glass-secondary anim-tap transition-colors p-1"
              aria-label={closeLabel ?? "Close modal"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      ) : (
        /* Header — name + status + setter + contacts flow on ONE line (Mako 2026-07-11);
          wraps only when the host pane is truly too narrow. */
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-6 py-3.5 border-b border-black/[0.08] min-w-0">
          <h2
            id="lead-timeline-modal-title"
            className="text-lg font-bold text-glass truncate min-w-0"
          >
            {lead?.name || email || "(no linked lead)"}
          </h2>
          {lead?.status && (
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge(lead.status)}`}
            >
              {lead.status}
            </span>
          )}
          {canonicalSetter && (
            <span
              data-testid="canonical-setter-header"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold"
              style={{
                // Diagonal hatch over a subtle red wash — reads like warning
                // tape, not a button. Sources both colors from --red token so
                // theme changes propagate (per CSS rules: no inline color
                // literals; use custom properties).
                color: "var(--red)",
                backgroundColor: "var(--red-bg)",
                backgroundImage:
                  "repeating-linear-gradient(135deg, color-mix(in srgb, var(--red) 18%, transparent) 0 1px, transparent 1px 6px)",
                borderRadius: "3px",
              }}
            >
              <span className="text-[9px] uppercase tracking-wider font-bold opacity-70">
                Setter
              </span>
              <span className="font-bold">{canonicalSetter.name ?? "—"}</span>
            </span>
          )}
          {/* Contact info in header — click any value to copy */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-glass-muted min-w-0">
            {email && (
              <span
                className="flex items-center gap-1 cursor-pointer select-none"
                onClick={() => copyToClipboard(email, setCopiedField, "email")}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {copiedField === "email" ? "Copied!" : email}
              </span>
            )}
            {lead?.phone && (
              <span
                className="flex items-center gap-1 cursor-pointer select-none"
                onClick={() => copyToClipboard(lead.phone!, setCopiedField, "phone")}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                {copiedField === "phone" ? "Copied!" : lead.phone}
              </span>
            )}
            {(() => {
              const rawIg = lead?.instagram || conversation?.lead_instagram;
              if (!rawIg) return null;
              const cleanIg = rawIg.replace(/^@+/, "");
              return (
                <span
                  className="flex items-center gap-1 cursor-pointer select-none"
                  onClick={() => copyToClipboard(cleanIg, setCopiedField, "instagram")}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                  {copiedField === "instagram" ? "Copied!" : `@${cleanIg}`}
                </span>
              );
            })()}
          </div>
          {/* X close button — only rendered when onClose is provided (modal). ml-auto
              pins it to the far right of the single header row. */}
          {onClose && (
            <button
              onClick={onClose}
              className="ml-auto text-glass-muted hover:text-glass-secondary anim-tap transition-colors p-1"
              aria-label={closeLabel ?? "Close modal"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Ask D: host-supplied slot (pool teammate-notes strip). Pinned under the
          header, flex-shrink-0 via its own wrapper, self-hides when empty. */}
      {notesSlot}

      {/* Call summary banner — pinned between header and panels, always visible
          (no scroll). Renders null unless the lead has call_booked events, so
          non-call leads (webinar/pre-call only) collapse the row entirely. */}
      {!isOrphan && <CallSummaryBanner timeline={timeline} timezone={timezone} />}

      {/* Step 5 (lead-phone-normalization plan): suggested phone-match banner —
          POINTER only, no merge/write action. Renders null when there are no
          suggested matches (same self-hiding contract as CallSummaryBanner). */}
      {!isOrphan && <SuggestedMatchBanner matches={suggestedMatches} onOpen={onOpenCompare} />}

      {/* Split Panel Content — columns are flex-1 with min-widths at lg: and up
          (≥1024px); when a column collapses (no DMs / no dials) the others absorb
          its width. If every min-width can't fit the host pane at lg:, the row
          scrolls horizontally instead of clipping content (unchanged desktop
          behavior). BELOW lg: (mobile/tablet, Mako 2026-07-13) the row stacks the
          three columns vertically (flex-col) — each column is w-full and sized to
          its own content, so the row itself becomes one scrollable document
          instead of 3 independently-scrolling side-by-side panes + a hidden
          horizontal overflow. This removes the need for a horizontal-scroll hint
          below lg: since nothing needs it anymore once stacked. */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto overflow-x-hidden lg:overflow-y-hidden lg:overflow-x-auto">
        {/* LEFT PANEL - Timeline (hidden in orphan mode) */}
        {!isOrphan && (
          <div className="w-full lg:flex-1 lg:min-w-[300px] border-b lg:border-b-0 lg:border-r border-black/[0.08] overflow-y-auto">
            <div className="px-5 py-4">
              <h3 className="text-sm font-semibold text-glass-muted uppercase tracking-wide mb-4">
                Timeline
              </h3>

              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-glass" />
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <p className="text-ds-red text-sm">{error}</p>
                  <button
                    onClick={fetchTimeline}
                    className="mt-2 text-sm text-ds-accent hover:text-ds-accent/80 anim-tap font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && data && (
                <>
                  {/* F5: Prior dialer — surfaced at the top of the timeline when set */}
                  {priorDialer && (
                    <section className="mb-3 px-3 py-2 liquid-glass-light anim-lift">
                      <p className="token-10u text-glass-muted uppercase mb-1">Prior dialer</p>
                      <p className="token-13 text-glass">
                        {priorDialer.name} — {priorDialer.attempts} attempt
                        {priorDialer.attempts === 1 ? "" : "s"} (last:{" "}
                        {new Date(priorDialer.lastAt).toLocaleString()})
                      </p>
                    </section>
                  )}

                  {mergedTimeline.length === 0 ? (
                    <p className="text-glass-muted text-center py-8 text-sm">
                      No events found for this lead.
                    </p>
                  ) : (
                    <div className="relative">
                      {/* Vertical line */}
                      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-black/[0.08]" />

                      <div className="space-y-4">
                        {renderTimeline.map((entry) => {
                          if (entry.kind === "reschedule") {
                            return (
                              <RescheduleItem
                                key={entry.key}
                                from={entry.from}
                                to={entry.to}
                                timezone={timezone}
                              />
                            );
                          }
                          if (entry.kind === "timeline") {
                            return (
                              <TimelineItem
                                key={entry.key}
                                event={entry.event}
                                timezone={timezone}
                                canonicalSetter={canonicalSetter}
                              />
                            );
                          }
                          // Dial (GHL call sync) entry — inline layout only
                          const de = entry.event;
                          const connected = de.status === "completed" || de.status === "answered";
                          return (
                            <div key={entry.key} className="relative pl-7">
                              {/* Dot */}
                              <div
                                className={`absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-black/[0.1] shadow-sm ${
                                  connected ? "bg-emerald-500" : "bg-slate-400"
                                }`}
                              />
                              <DialCard
                                dial={de}
                                dialerRole={roleOf(de.dialer_id)}
                                timezone={timezone}
                                canViewRecordings={canViewRecordings}
                                leadName={lead?.name ?? null}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* MIDDLE/RIGHT PANEL - Tabbed (DMs / Transcript). Full width in orphan mode.
            Collapses to a slim greyed strip when the lead has no DM data — the strip
            expands on click so Resync stays reachable. */}
        {dmsCollapsed ? (
          <button
            type="button"
            data-testid="dms-collapsed-strip"
            data-has-data={hasDms ? "true" : "false"}
            onClick={() => setDmsExpanded(true)}
            title={
              hasDms
                ? `${messages.length} Instagram DM${messages.length === 1 ? "" : "s"} — click to expand`
                : "No Instagram DMs for this lead — click to expand"
            }
            className={[
              // Below lg: (stacked layout) render as a full-width horizontal bar so it
              // reads like a normal collapsed section, not a sideways sliver. At lg: and
              // up this is unchanged — the narrow w-9 vertical rail from before. No border
              // of its own — Timeline's border-b (above) already divides the stack, same
              // as desktop where the middle section has no border of its own either.
              "w-full lg:w-9 flex-none flex flex-row lg:flex-col items-center justify-center gap-2 py-2 lg:py-0 transition-colors",
              hasDms
                ? "bg-ds-green/10 text-ds-green hover:bg-ds-green/20"
                : "bg-black/[0.02] text-glass-inactive hover:bg-black/[0.05] hover:text-glass-muted",
            ].join(" ")}
          >
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span className="text-[10px] font-semibold uppercase tracking-widest lg:[writing-mode:vertical-rl] whitespace-nowrap">
              {hasDms ? `Instagram · ${messages.length}` : "Instagram · no DMs"}
            </span>
          </button>
        ) : (
          <div
            className={`${isOrphan ? "w-full" : "w-full lg:flex-1 lg:min-w-[280px]"} flex flex-col bg-black/[0.03]`}
          >
            {/* Tab control row — replaces old "Instagram DMs" static header */}
            <div className="flex border-b border-black/[0.08] bg-black/[0.03]">
              {!isOrphan && (
                <button
                  onClick={() => {
                    // Pool + modal: clicking the active DMs header re-collapses the column
                    // back to its strip (Mako 2026-07-11). Call-search (anchored/orphan) and
                    // inline call sites: unchanged tab switch.
                    if (dmsCanCollapse && activeTab === "dms") {
                      setDmsExpanded(false);
                    } else {
                      setActiveTab("dms");
                    }
                  }}
                  title={dmsCanCollapse ? "Click to collapse" : undefined}
                  className={`px-4 py-3 text-sm font-semibold uppercase tracking-wide transition ${
                    activeTab === "dms"
                      ? "text-glass border-b-2 border-ds-accent"
                      : "text-glass-muted hover:text-glass"
                  }`}
                >
                  Instagram DMs
                  {messages.length > 0 && activeTab === "dms" && (
                    <span className="ml-1 text-xs font-normal normal-case tracking-normal">
                      ({messages.length})
                    </span>
                  )}
                  {dmsCanCollapse && activeTab === "dms" && (
                    <span className="ml-1.5 text-xs font-normal" aria-hidden="true">
                      ‹
                    </span>
                  )}
                </button>
              )}
              {anchorRecordingId && (
                <button
                  onClick={() => setActiveTab("transcript")}
                  className={`px-4 py-3 text-sm font-semibold uppercase tracking-wide transition ${
                    activeTab === "transcript"
                      ? "text-glass border-b-2 border-ds-accent"
                      : "text-glass-muted hover:text-glass"
                  }`}
                >
                  Transcript
                </button>
              )}
              {/* DMs actions toolbar — only visible on DMs tab */}
              {activeTab === "dms" && !isOrphan && (
                <div className="ml-auto flex items-center gap-2 pr-3">
                  {/* Copy Conversation Button */}
                  <CopyConversationButton
                    getText={() =>
                      serializeConversation({
                        messages,
                        contactName: conversation?.contact_name || email || "",
                        leadInstagram: conversation?.lead_instagram,
                        timezone,
                        orgName: dataCtx?.currentOrganization?.name,
                      })
                    }
                    variant="header"
                    disabled={messages.length === 0 || dmLoading}
                  />
                  {conversation && (
                    <button
                      onClick={handleResync}
                      disabled={isResyncing}
                      className={`px-2 py-1 text-xs font-medium anim-tap rounded transition-colors ${
                        isResyncing
                          ? "bg-black/[0.05] text-glass-muted cursor-not-allowed"
                          : resyncSuccess
                            ? "bg-ds-green-bg text-ds-green"
                            : resyncError
                              ? "bg-ds-red/15 text-ds-red"
                              : "bg-black/[0.05] text-glass-secondary hover:bg-ds-accent/15 hover:text-ds-accent"
                      }`}
                      title="Fetch latest messages from GHL"
                    >
                      {isResyncing ? (
                        <span className="flex items-center gap-1">
                          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Syncing
                        </span>
                      ) : resyncSuccess ? (
                        <span className="flex items-center gap-1">{resyncSuccess}</span>
                      ) : resyncError ? (
                        <span className="flex items-center gap-1">Failed</span>
                      ) : (
                        <span className="flex items-center gap-1">Resync</span>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* DM Messages — only when activeTab === 'dms' and not orphan */}
            {activeTab === "dms" && !isOrphan && (
              <>
                <div className="flex-1 overflow-y-auto p-4">
                  {dmLoading && (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ds-accent" />
                    </div>
                  )}

                  {!dmLoading && dmError && (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <svg
                        className="w-12 h-12 text-glass-inactive mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                      <p className="text-glass-muted text-sm">{dmError}</p>
                      <p className="text-glass-muted text-xs mt-1">
                        Lead may not have an Instagram conversation yet
                      </p>
                    </div>
                  )}

                  {!dmLoading && !dmError && messages.length === 0 && conversation && (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <svg
                        className="w-12 h-12 text-glass-inactive mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                      <p className="text-glass-muted text-sm">No messages in this conversation</p>
                      <button
                        onClick={handleResync}
                        className="mt-2 text-xs text-ds-accent hover:text-ds-accent/80 anim-tap font-medium"
                      >
                        Try syncing from GHL
                      </button>
                    </div>
                  )}

                  {!dmLoading &&
                    !dmError &&
                    messages.map((msg) => {
                      const isHighlighted = highlightTimestamp
                        ? Math.abs(
                            new Date(msg.sent_at).getTime() - new Date(highlightTimestamp).getTime()
                          ) <= 60_000
                        : false;
                      return (
                        <ChatBubble
                          key={msg.id}
                          ref={isHighlighted ? highlightRef : undefined}
                          message={msg}
                          timezone={timezone}
                          highlighted={isHighlighted}
                          ownerName={
                            ghlUserNames[
                              msg.owner_at_send ?? conversation?.ghl_assigned_user_id ?? ""
                            ] ?? null
                          }
                        />
                      );
                    })}

                  <div ref={messagesEndRef} />
                </div>

                {/* Footer: Setter + GHL Contact ID */}
                {conversation && (
                  <div className="px-4 py-2 border-t border-black/[0.08] flex items-center justify-between text-xs text-glass-muted">
                    {conversation.ghl_contact_id && ghlLocationId ? (
                      <a
                        href={`https://app.gohighlevel.com/v2/location/${ghlLocationId}/contacts/detail/${conversation.ghl_contact_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-ds-accent hover:underline"
                      >
                        {conversation.ghl_contact_id}
                      </a>
                    ) : (
                      <span className="font-mono opacity-70">
                        {conversation.ghl_contact_id || "—"}
                      </span>
                    )}
                    {assignedSetterName && <span>Setter: {assignedSetterName}</span>}
                  </div>
                )}
              </>
            )}

            {/* Transcript tab content */}
            {activeTab === "transcript" && anchorRecordingId && organizationId && (
              <div className="flex-1 overflow-y-auto">
                <TranscriptPanel
                  recordingId={anchorRecordingId}
                  organizationId={organizationId}
                  orgTz={orgTz ?? timezone}
                  anchorTurnIndex={anchorTurnIndex}
                />
              </div>
            )}

            {/* Orphan mode with missing organizationId — should not happen in practice */}
            {activeTab === "transcript" && anchorRecordingId && !organizationId && (
              <div className="flex items-center justify-center flex-1 py-12">
                <p className="text-glass-muted text-sm">
                  organizationId required to load transcript.
                </p>
              </div>
            )}
          </div>
        )}

        {/* THIRD COLUMN - Dials (column layout only). Newest first — the last
            attempt is the one a manager checks. Cards shared with the inline
            layout via DialCard. Collapses to a greyed strip when no dials exist. */}
        {showDialsColumn && dialsCollapsed && (
          <button
            type="button"
            data-testid="dials-collapsed-strip"
            data-has-data={hasDials ? "true" : "false"}
            onClick={() => setDialsExpanded(true)}
            title={
              hasDials
                ? `${dialEvents.length} dial${dialEvents.length === 1 ? "" : "s"} — click to expand`
                : "No dials synced for this lead — click to expand"
            }
            className={[
              // Same lg: bar/rail split as the DMs collapsed strip above.
              "w-full lg:w-9 flex-none border-t lg:border-t-0 lg:border-l border-black/[0.08] flex flex-row lg:flex-col items-center justify-center gap-2 py-2 lg:py-0 transition-colors",
              hasDials
                ? "bg-ds-green/10 text-ds-green hover:bg-ds-green/20"
                : "bg-black/[0.02] text-glass-inactive hover:bg-black/[0.05] hover:text-glass-muted",
            ].join(" ")}
          >
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            <span className="text-[10px] font-semibold uppercase tracking-widest lg:[writing-mode:vertical-rl] whitespace-nowrap">
              {hasDials ? `Dials · ${dialEvents.length}` : "Dials · none"}
            </span>
          </button>
        )}
        {showDialsColumn && !dialsCollapsed && (
          <div
            ref={dialsColumnRef}
            data-testid="dials-column"
            className="w-full lg:flex-1 lg:min-w-[260px] border-t lg:border-t-0 lg:border-l border-black/[0.08] overflow-y-auto"
          >
            <div className="px-5 py-4">
              {/* Pool: header is a button that re-collapses the Dials column back to
                  its strip (Mako 2026-07-11). Modal/call-search: static h3 (no strip). */}
              {railsCollapsedByDefault ? (
                <button
                  type="button"
                  onClick={() => setDialsExpanded(false)}
                  title="Click to collapse"
                  className="text-sm font-semibold text-glass-muted uppercase tracking-wide mb-4 flex items-center gap-1 cursor-pointer hover:text-glass transition-colors"
                >
                  Dials
                  {dialEvents.length > 0 && (
                    <span className="ml-1 text-xs font-normal normal-case tracking-normal">
                      ({dialEvents.length})
                    </span>
                  )}
                  <span className="text-xs font-normal" aria-hidden="true">
                    ‹
                  </span>
                </button>
              ) : (
                <h3 className="text-sm font-semibold text-glass-muted uppercase tracking-wide mb-4">
                  Dials
                  {dialEvents.length > 0 && (
                    <span className="ml-1 text-xs font-normal normal-case tracking-normal">
                      ({dialEvents.length})
                    </span>
                  )}
                </h3>
              )}
              {dialEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <svg
                    className="w-12 h-12 text-glass-inactive mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <p className="text-glass-muted text-sm">No dials synced for this lead</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Fetch order is started_at ASC — render as-is so the latest
                      dial sits at the bottom, matching the DM chat (Mako 2026-07-14) */}
                  {dialEvents.map((dial) => (
                    <DialCard
                      key={dial.id}
                      dial={dial}
                      dialerRole={roleOf(dial.dialer_id)}
                      timezone={timezone}
                      canViewRecordings={canViewRecordings}
                      leadName={lead?.name ?? null}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Keys already rendered elsewhere or internal — skip from custom_data display
const HIDDEN_CUSTOM_KEYS = new Set([
  "closer_name",
  "setter_name",
  "fathom_link",
  "source_platform",
  "web_signup_source",
  "outcome_notes",
  "quality_of_lead",
  // Phase 8 — AI summary keys hidden from generic short/long bucketing.
  // PreCallFormDetails (below) reads them directly from custom_data, bypassing this filter.
  "ai_client_status",
  "ai_closing_readiness",
  "ai_financial_status",
  "ai_model",
  "ai_processed_at",
  // Noise / redundant — never worth a grid row. a1-a4 = Calendly booking
  // answers (a1 attendance "yes" always, a2 source, a3 phone, a4 IG handle —
  // phone+IG are already in the header); invitee_* duplicate the lead identity;
  // google_form_response_id is an internal ref. Verified noise (2026-06-20).
  "google_form_response_id",
  "a1",
  "a2",
  "a3",
  "a4",
  "invitee_email",
  "invitee_name",
  // self_set_claim audit keys (claim_lead RPC) — internal bookkeeping, not
  // timeline content. `note` (the closer's claim note) stays visible.
  "claimed_at",
  "lead_email",
  "commission_pct",
]);

// Short fields shown as key-value pairs at the top
const SHORT_FIELD_LABELS: Record<string, string> = {
  priority: "Priority",
  invest: "Invest",
  investment: "Investment",
  warmth_level: "Warmth",
  notes: "Notes",
  event_name: "Event",
  event_type_name: "Event Type",
};

function formatCustomKey(key: string): string {
  return SHORT_FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isShortValue(value: unknown): boolean {
  if (typeof value !== "string") return true;
  return value.length < 80;
}

function formatDateTime(d: string | null, timezone: string): string {
  if (!d) return "--";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return (
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: timezone,
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    })
  );
}

/** Compact date+time, no year — e.g. "Jun 9, 7:00 PM". Used in the reschedule track. */
function formatDateTimeShort(d: string | null, timezone: string): string {
  if (!d) return "--";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return (
    date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: timezone }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    })
  );
}

// =============================================================================
// CALL SUMMARY BANNER
// =============================================================================
// PROBLEM IT SOLVES: call_booked / call_cancelled cards each carry two dates
// that disagree — event_date (when the record was logged) and call_start_time
// (when the call is scheduled). A reschedule fires a cancel + a new booking at
// the SAME event_date instant, so a stack of cards reads as chaos. This banner
// answers "when is/was the call" in one pinned line, with the reschedule chain.
//
// DETERMINISTIC, NO GUESSING: get_lead_timeline carries no reschedule link
// tying a specific cancel to its rebook, so we DO NOT pair them. We render the
// chronological sequence of booked slots and mark the latest as active. The
// call counts as "cancelled" only if the latest cancellation is STRICTLY after
// the latest booking — a tie means a reschedule, since GHL logs the cancel +
// the new booking at the same event_date instant (verified on real data:
// manfailee02@gmail.com has two such simultaneous cancel+rebook pairs).
// =============================================================================

type CallSummaryState = "upcoming" | "completed" | "cancelled";

const CALL_STATE_STYLES: Record<CallSummaryState, { wrap: string; chip: string; label: string }> = {
  upcoming: {
    wrap: "bg-ds-accent/10 border-ds-accent/30",
    chip: "text-ds-accent",
    label: "Next Call",
  },
  completed: {
    wrap: "bg-ds-green-bg border-ds-green/30",
    chip: "text-ds-green",
    label: "Call",
  },
  cancelled: {
    wrap: "bg-ds-amber-bg border-ds-amber/30",
    chip: "text-ds-amber",
    label: "Call Cancelled",
  },
};

const CallSummaryBanner: React.FC<{ timeline: LeadTimelineEvent[]; timezone: string }> = ({
  timeline,
  timezone,
}) => {
  const summary = useMemo(() => {
    const bookings = timeline.filter((e) => e.event_type === "call_booked");
    if (bookings.length === 0) return null;

    const cancellations = timeline.filter((e) => e.event_type === "call_cancelled");
    const outcomes = timeline.filter((e) => e.event_type === "sales_call_outcome");
    const byDateAsc = (a: LeadTimelineEvent, b: LeadTimelineEvent) =>
      (a.event_date ?? "").localeCompare(b.event_date ?? "");

    const sortedBookings = [...bookings].sort(byDateAsc);
    const activeBooking = sortedBookings[sortedBookings.length - 1];
    const activeSlot = activeBooking.call_start_time ?? activeBooking.call_booked_time;
    const sortedOutcomes = [...outcomes].sort(byDateAsc);
    const outcome = sortedOutcomes.length ? sortedOutcomes[sortedOutcomes.length - 1] : null;
    const sortedCancels = [...cancellations].sort(byDateAsc);
    const latestCancel = sortedCancels.length ? sortedCancels[sortedCancels.length - 1] : null;

    // The call is OFF only if the latest cancellation is STRICTLY after the
    // latest booking. A tie = reschedule (GHL logs cancel + rebook together).
    const latestBookingDate = activeBooking.event_date ?? "";
    const latestCancelDate = latestCancel?.event_date ?? "";
    const cancelledFinal = latestCancel != null && latestCancelDate > latestBookingDate;

    let state: CallSummaryState;
    let primaryTime: string | null;
    let closerName: string | null;
    if (outcome) {
      state = "completed";
      // Prefer the final booking's slot — sales_call_outcome.call_start_time is
      // often a midnight/default value, not the real call time (verified).
      primaryTime = activeSlot ?? outcome.call_start_time;
      closerName = activeBooking.closer_name ?? outcome.closer_name;
    } else if (cancelledFinal && latestCancel) {
      state = "cancelled";
      primaryTime = latestCancel.call_start_time ?? activeSlot;
      closerName = activeBooking.closer_name;
    } else {
      state = "upcoming";
      primaryTime = activeSlot;
      closerName = activeBooking.closer_name;
    }

    // Outcome-derived facts for the at-a-glance row. A refund zeroes
    // cash_collected/revenue_closed on the row, so read the snapshotted
    // pre_refund_* amounts when refunded — otherwise the banner would say "$0"
    // on a real (clawed-back) close. Refund signal = custom_data.refunded
    // (the only reliable marker; status flips to 'unique', which is NOT a
    // refund signal on its own). fathom_link = direct call recording.
    const ocd = (outcome?.custom_data ?? {}) as Record<string, unknown>;
    const isRefunded = ocd.refunded === true || ocd.refunded === "true";
    const dealCash = isRefunded ? Number(ocd.pre_refund_cash) || 0 : (outcome?.cash_collected ?? 0);
    const dealRev = isRefunded
      ? Number(ocd.pre_refund_revenue) || 0
      : (outcome?.revenue_closed ?? 0);
    const fathomLink =
      typeof ocd.fathom_link === "string" && ocd.fathom_link ? ocd.fathom_link : null;

    return {
      state,
      primaryTime,
      closerName,
      outcomeStatus: outcome?.status ?? null,
      cancelBy: state === "cancelled" ? (latestCancel?.call_cancel_by ?? null) : null,
      rescheduleCount: Math.max(0, sortedBookings.length - 1),
      dealCash,
      dealRev,
      isRefunded,
      fathomLink,
    };
  }, [timeline]);

  if (!summary) return null;
  const styles = CALL_STATE_STYLES[summary.state];
  const chipText =
    summary.state === "completed" && summary.outcomeStatus
      ? summary.outcomeStatus.replace(/_/g, " ")
      : styles.label;

  return (
    <div
      data-testid="call-summary-banner"
      data-call-state={summary.state}
      className={`flex items-center gap-2 flex-wrap px-6 py-1.5 border-b border-black/[0.08] text-sm ${styles.wrap}`}
    >
      {/* Eyebrow — names what this pinned banner IS (Mako 2026-07-11): the lead's
          most recent call interaction, so "CALL CANCELLED / LOST" reads as a state,
          not a mystery. */}
      <span className="text-[10px] font-bold uppercase tracking-wider text-glass-muted">
        Last interaction
      </span>
      <span className="text-glass-muted">·</span>
      <span className={`text-[11px] font-bold uppercase tracking-wider ${styles.chip}`}>
        {chipText}
      </span>

      <span className="text-glass-muted">·</span>
      <span className="font-semibold text-glass" data-testid="call-summary-time">
        {summary.state === "cancelled" && (
          <span className="text-glass-muted font-normal">was </span>
        )}
        {formatDateTime(summary.primaryTime, timezone)}
        <span className="text-[9px] text-glass-muted ml-0.5">({getTimezoneAbbr(timezone)})</span>
      </span>

      {summary.closerName && (
        <>
          <span className="text-glass-muted">·</span>
          <span className="text-glass-secondary">{summary.closerName}</span>
        </>
      )}

      {summary.cancelBy && (
        <>
          <span className="text-glass-muted">·</span>
          <span className="text-xs text-glass-muted">by {summary.cancelBy}</span>
        </>
      )}

      {(summary.dealCash > 0 || summary.dealRev > 0) && (
        <>
          <span className="text-glass-muted">·</span>
          <span
            data-testid="call-summary-value"
            className={`font-semibold ${summary.isRefunded ? "text-ds-red line-through" : "text-glass"}`}
          >
            {formatCurrencyOrEmpty(summary.dealCash)}
            {summary.dealRev > 0 && summary.dealRev !== summary.dealCash && (
              <span className="text-glass-muted ml-1">
                / {formatCurrencyOrEmpty(summary.dealRev)} rev
              </span>
            )}
          </span>
        </>
      )}

      {summary.isRefunded && (
        <span
          data-testid="call-summary-refunded"
          className="inline-flex items-center rounded border border-ds-red/30 bg-ds-red/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ds-red"
        >
          Refunded
        </span>
      )}

      {summary.fathomLink && (
        <a
          href={summary.fathomLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="watch-call-link"
          className="font-medium text-ds-accent hover:underline"
        >
          Watch call
        </a>
      )}

      {summary.rescheduleCount > 0 && (
        <span
          data-testid="call-reschedule-count"
          className="ml-auto whitespace-nowrap rounded-full bg-black/[0.05] px-2 py-0.5 text-[10px] font-semibold text-glass-secondary"
        >
          rescheduled {summary.rescheduleCount}×
        </span>
      )}
    </div>
  );
};

// =============================================================================
// SUGGESTED MATCH BANNER (Step 5, lead-phone-normalization plan)
// =============================================================================
// Small sibling of CallSummaryBanner, same pinned-strip idiom, mounted right
// after it. Backed by get_suggested_lead_matches(p_email, p_org_id) — other
// leads in this org sharing this lead's canonical phone number (blocklist
// numbers and clusters >3 leads already excluded server-side, see
// sql/get_suggested_lead_matches.sql). POINTER ONLY: no merge button, no write
// path. Renders null when there are no suggested matches, same self-hiding
// contract as CallSummaryBanner (non-call leads collapse that row entirely).
// =============================================================================

const SuggestedMatchBanner: React.FC<{
  matches: SuggestedLeadMatch[];
  onOpen?: (m: SuggestedLeadMatch) => void;
}> = ({ matches, onOpen }) => {
  if (!matches || matches.length === 0) return null;

  return (
    <div
      data-testid="suggested-match-banner"
      className="flex items-center gap-2 flex-wrap px-6 py-1.5 border-b border-black/[0.08] text-sm bg-ds-accent/10 border-ds-accent/30"
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-glass-muted">
        Suggested Lead Match
      </span>
      <span className="text-glass-muted">·</span>
      <span className="text-[11px] font-bold uppercase tracking-wider text-ds-accent">
        {matches.length} other lead{matches.length === 1 ? "" : "s"}
      </span>
      <span className="text-glass-muted">shares this phone number:</span>
      {matches.map((m, i) => (
        <React.Fragment key={m.id}>
          {i > 0 && <span className="text-glass-muted">,</span>}
          {onOpen ? (
            <button
              type="button"
              data-testid="suggested-match-open"
              onClick={() => onOpen(m)}
              className="font-semibold text-ds-accent hover:underline anim-tap"
            >
              {m.name || m.email.split("@")[0]}
              <span className="text-glass-muted font-normal ml-1">({m.email})</span>
            </button>
          ) : (
            <span className="font-semibold text-glass" data-testid="suggested-match-item">
              {m.name || m.email.split("@")[0]}
              <span className="text-glass-muted font-normal ml-1">({m.email})</span>
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

type CanonicalSetter = {
  id: string;
  name: string | null;
  role: LeadTimelineEvent["setter_role"];
  event_date: string | null;
} | null;

type PrecallSection = { title: string; keys: string[] };

const PRECALL_SECTIONS: PrecallSection[] = [
  { title: "PRE-CALL NOTE", keys: ["setter_name", "platform_source"] },
  { title: "FINANCIALS & COMMITMENT", keys: ["investment_amount", "ai_financial_status"] },
  { title: "STATUS & READINESS", keys: ["ai_client_status", "ai_closing_readiness"] },
  { title: "RAW CONTEXT", keys: ["context_notes"] },
];

const SECTION_OWNED_KEYS = new Set(PRECALL_SECTIONS.flatMap((s) => s.keys));
const AI_PROSE_KEYS = new Set([
  "ai_client_status",
  "ai_closing_readiness",
  "ai_financial_status",
  "context_notes",
]);

const PreCallFormDetails: React.FC<{ event: LeadTimelineEvent }> = ({ event }) => {
  const cd = (event.custom_data ?? {}) as Record<string, unknown>;
  const renderableSections = PRECALL_SECTIONS.map((section) => ({
    ...section,
    present: section.keys.filter((k) => cd[k] != null && cd[k] !== "" && cd[k] !== "-"),
  })).filter((s) => s.present.length > 0);
  const unmapped = Object.entries(cd).filter(
    ([k, v]) => !SECTION_OWNED_KEYS.has(k) && !HIDDEN_CUSTOM_KEYS.has(k) && v != null && v !== ""
  );

  if (renderableSections.length === 0 && unmapped.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-black/[0.04] space-y-3">
      {renderableSections.map((section) => (
        <div key={section.title}>
          <div className="token-10u uppercase tracking-wide text-glass-muted mb-1">
            {section.title}
          </div>
          <div className="space-y-1 text-xs">
            {section.present.map((key) => {
              const value = String(cd[key]);
              const isProse = AI_PROSE_KEYS.has(key);
              return (
                <div key={key} className="min-w-0">
                  <span className="text-glass-muted">{formatCustomKey(key)}: </span>
                  <span
                    className={`text-glass-secondary font-medium ${isProse ? "whitespace-pre-wrap break-words" : "break-words [overflow-wrap:anywhere]"}`}
                  >
                    {value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {unmapped.length > 0 && (
        <div>
          <div className="token-10u uppercase tracking-wide text-glass-muted mb-1">OTHER</div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-x-4 gap-y-1 text-xs">
            {unmapped.map(([k, v]) => (
              <div key={k} className="min-w-0">
                <span className="text-glass-muted">{formatCustomKey(k)}: </span>
                <span className="text-glass-secondary font-medium break-words [overflow-wrap:anywhere]">
                  {String(v)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// One reschedule beat — fuses a cancelled (old) slot + a rebooked (new) slot
// into a single row so the move reads as one event, not two confusing cards.
const RescheduleItem: React.FC<{
  from: LeadTimelineEvent;
  to: LeadTimelineEvent;
  timezone: string;
}> = ({ from, to, timezone }) => {
  const fromTime = from.call_start_time ?? from.call_booked_time;
  const toTime = to.call_start_time ?? to.call_booked_time;
  const closer = to.closer_name ?? from.closer_name;
  return (
    <div className="relative pl-7" data-testid="reschedule-card">
      {/* Dot */}
      <div className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-black/[0.1] bg-ds-amber shadow-sm" />
      <div className="bg-black/[0.03] border border-black/[0.04] rounded-lg p-3 hover:border-black/[0.08] transition-colors">
        <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-0.5 mb-1.5">
          <span className="px-2 py-0.5 rounded text-xs font-medium border bg-ds-amber/10 text-ds-amber border-ds-amber/20">
            Rescheduled
          </span>
          <span className="text-xs text-glass-muted">
            {formatDateTime(to.event_date, timezone)}
            <span className="text-[9px] ml-0.5">({getTimezoneAbbr(timezone)})</span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-glass-muted line-through">
            {formatDateTimeShort(fromTime, timezone)}
          </span>
          <span className="text-glass-muted">→</span>
          <span className="text-glass-secondary font-semibold">
            {formatDateTimeShort(toTime, timezone)}
          </span>
          <span className="text-[9px] text-glass-muted">({getTimezoneAbbr(timezone)})</span>
        </div>
        {closer && (
          <p className="mt-1 text-xs text-glass-muted">
            w/ <span className="text-glass-secondary">{closer}</span>
          </p>
        )}
        {/* Why the move — pulled from the cancelled half (from) that we fused
            away. Suppress Calendly's "Rescheduled from connected calendar event"
            boilerplate (it only restates "this was a reschedule"); show real
            reasons ("Preferred time", "Double booking", "until done talking
            with team"). call_cancel_by tells who moved it (Host/Invitee). */}
        {(() => {
          const reason = (from.call_cancel_reason ?? "").trim();
          const isBoilerplate = /^rescheduled from connected calendar event$/i.test(reason);
          if (!reason || isBoilerplate) return null;
          return (
            <p className="mt-1 text-xs text-glass-muted" data-testid="reschedule-reason">
              Reason: <span className="text-glass-secondary">{reason}</span>
              {from.call_cancel_by && (
                <span className="text-glass-muted"> · by {from.call_cancel_by}</span>
              )}
            </p>
          );
        })()}
      </div>
    </div>
  );
};

const TimelineItem: React.FC<{
  event: LeadTimelineEvent;
  timezone: string;
  canonicalSetter: CanonicalSetter;
}> = ({ event, timezone, canonicalSetter }) => {
  const [expanded, setExpanded] = useState(false);
  const label = EVENT_LABELS[event.event_type] || event.event_type.replace(/_/g, " ");
  const colorClass =
    EVENT_COLORS[event.event_type] || "bg-black/[0.05] text-glass-secondary border-black/[0.08]";
  const dotColor = DOT_COLORS[event.event_type] || "bg-black/[0.1]";

  const hasMoney = event.cash_collected > 0 || event.revenue_closed > 0;
  // Parse custom_data into short fields and long-form fields
  const { shortFields, longFields } = useMemo(() => {
    const shorts: { key: string; value: string }[] = [];
    const longs: { key: string; value: string }[] = [];
    if (!event.custom_data) return { shortFields: shorts, longFields: longs };

    for (const [key, val] of Object.entries(event.custom_data)) {
      if (HIDDEN_CUSTOM_KEYS.has(key)) continue;
      if (val == null || val === "") continue;
      const strVal = typeof val === "object" ? JSON.stringify(val) : String(val);
      if (isShortValue(strVal)) {
        shorts.push({ key, value: strVal });
      } else {
        longs.push({ key, value: strVal });
      }
    }
    return { shortFields: shorts, longFields: longs };
  }, [event.custom_data]);

  const hasCustomData = shortFields.length > 0 || longFields.length > 0;

  return (
    <div className="relative pl-7" data-testid="event-card" data-event-type={event.event_type}>
      {/* Dot */}
      <div
        className={`absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-black/[0.1] ${dotColor} shadow-sm`}
      />

      <div className="bg-black/[0.03] border border-black/[0.04] rounded-lg p-3 hover:border-black/[0.08] transition-colors">
        {/* Top row: badge + date — wraps below the badge when the column is
            narrow instead of the date clipping into the pill */}
        <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-0.5 mb-1.5">
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium border flex-shrink-0 ${colorClass}`}
          >
            {label}
          </span>
          <span className="text-xs text-glass-muted whitespace-nowrap">
            {formatDateTime(event.event_date, timezone)}
            <span className="text-[9px] ml-0.5">({getTimezoneAbbr(timezone)})</span>
          </span>
        </div>

        {/* Status + money */}
        <div className="flex items-center gap-2 flex-wrap">
          {event.status && (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(event.status)}`}
            >
              {event.status.replace(/_/g, " ")}
            </span>
          )}
          {hasMoney && (
            <span className="text-xs font-medium text-glass-secondary">
              {formatCurrencyOrEmpty(event.cash_collected)}
              {event.revenue_closed > 0 && event.revenue_closed !== event.cash_collected && (
                <span className="text-glass-muted ml-1">
                  / {formatCurrencyOrEmpty(event.revenue_closed)} rev
                </span>
              )}
            </span>
          )}
          {/* Lead's source as the closer logged it on the outcome — hidden from
              the generic grid (HIDDEN_CUSTOM_KEYS), rendered here. No icon —
              the "via" label carries it. */}
          {event.event_type === "sales_call_outcome" &&
            typeof (event.custom_data as Record<string, unknown> | null)?.source_platform ===
              "string" && (
              <span data-testid="outcome-source" className="text-[11px] text-glass-muted">
                via {String((event.custom_data as Record<string, unknown>).source_platform)}
              </span>
            )}
          {/* Per-call recording link. The banner only carries the LATEST
              outcome's link, but a lead can have multiple calls — so each
              sales_call_outcome card surfaces its OWN fathom_link. */}
          {event.event_type === "sales_call_outcome" &&
            (() => {
              const link = (event.custom_data as Record<string, unknown> | null)?.fathom_link;
              if (typeof link !== "string" || !link) return null;
              return (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="card-watch-call"
                  className="text-[11px] font-medium text-ds-accent hover:underline"
                >
                  Watch call
                </a>
              );
            })()}
        </div>

        {/* Details */}
        <div className="mt-1.5 text-xs text-glass-muted space-y-0.5">
          {event.sales_program_sold && (
            <p className="text-glass-secondary font-medium">{event.sales_program_sold}</p>
          )}
          {event.closer_name && (
            <p>
              Closer: <span className="text-glass-secondary">{event.closer_name}</span>
            </p>
          )}
          {event.setter_name &&
            (() => {
              // Pill matches the SINGLE winning event by (setter_id, event_date) pair
              // so multiple pre-calls from the same person don't all light up green.
              const isCanonicalEvent =
                event.event_type === "sales_pre_call" &&
                canonicalSetter != null &&
                event.setter_id === canonicalSetter.id &&
                event.event_date === canonicalSetter.event_date;
              const isDemotedDialerPreCall =
                event.event_type === "sales_pre_call" &&
                event.setter_role === "dialer" &&
                !isCanonicalEvent;
              // closer/manager/exec/client filed a pre-call — ineligible for canonical
              // setter under the trigger's filter. Tag visually so they don't render
              // identical to a setter pre-call.
              const isNonAttributionPreCall =
                event.event_type === "sales_pre_call" &&
                event.setter_role !== "setter" &&
                event.setter_role !== "dialer" &&
                !isCanonicalEvent;
              return (
                <p>
                  {isCanonicalEvent && (
                    <span
                      data-testid="setter-canonical-pill"
                      className="inline-flex items-center px-1.5 py-0.5 mr-1.5 text-[9px] font-bold uppercase tracking-wide rounded bg-ds-green/15 text-ds-green border border-ds-green/30"
                    >
                      Setter
                    </span>
                  )}
                  Setter: <span className="text-glass-secondary">{event.setter_name}</span>
                  {isDemotedDialerPreCall && (
                    <span className="ml-1.5 text-[9px] text-glass-muted">(dialer)</span>
                  )}
                  {isNonAttributionPreCall && (
                    <span className="ml-1.5 text-[9px] text-glass-muted">
                      ({event.setter_role ?? "no role"})
                    </span>
                  )}
                </p>
              );
            })()}
          {event.call_start_time && (
            <p>
              Scheduled:{" "}
              <span className="text-glass-secondary">
                {formatDateTime(event.call_start_time, timezone)}
                <span className="text-[9px] text-glass-muted ml-0.5">
                  ({getTimezoneAbbr(timezone)})
                </span>
              </span>
              {event.closer_name && (
                <span className="text-glass-secondary"> w/ {event.closer_name}</span>
              )}
            </p>
          )}
          {event.call_booked_time && !event.call_start_time && (
            <p>
              Booked for:{" "}
              <span className="text-glass-secondary">
                {formatDate(event.call_booked_time, timezone)}
              </span>
            </p>
          )}
          {event.call_cancel_by && (
            <p>
              Cancelled by: <span className="text-glass-secondary">{event.call_cancel_by}</span>
            </p>
          )}
          {event.call_cancel_reason && (
            <p>
              Reason: <span className="text-glass-secondary">{event.call_cancel_reason}</span>
            </p>
          )}
          {event.sales_next_payment && (
            <p>
              Next payment:{" "}
              <span className="text-glass-secondary">
                {formatDate(event.sales_next_payment, timezone)}
              </span>
              {event.sales_next_payment_amount != null && (
                <span className="text-glass-secondary">
                  {" "}
                  ({formatCurrencyOrEmpty(event.sales_next_payment_amount)})
                </span>
              )}
            </p>
          )}
          {event.web_signup_source && (
            <p>
              Source: <span className="text-glass-secondary">{event.web_signup_source}</span>
            </p>
          )}
        </div>

        {/* Closer's note — the free-text "why" behind the outcome. Lives in
            custom_data.outcome_notes (kept in HIDDEN_CUSTOM_KEYS so it does not
            also render as a raw key in the generic grid below — same
            render-explicitly pattern as the Phase 8 AI-summary keys).
            Most load-bearing for status='unique', where the status word alone
            says nothing — the closer's note is the only place the real outcome
            lives. Always visible (not behind "See more") because it IS the
            outcome story. */}
        {event.event_type === "sales_call_outcome" &&
          (() => {
            const note = (event.custom_data as Record<string, unknown> | null)?.outcome_notes;
            if (typeof note !== "string" || !note.trim()) return null;
            return (
              <div className="mt-2 pt-2 border-t border-black/[0.04]" data-testid="outcome-note">
                <div className="token-10u uppercase tracking-wide text-glass-muted mb-1">
                  {"Closer's Note"}
                </div>
                <p className="text-xs text-glass-secondary whitespace-pre-wrap break-words leading-relaxed">
                  {note.trim()}
                </p>
              </div>
            );
          })()}

        {/* Custom Data — Pre-call / Form Submission Details */}
        {event.event_type === "sales_pre_call" ? (
          <PreCallFormDetails event={event} />
        ) : hasCustomData ? (
          <div className="mt-2 pt-2 border-t border-black/[0.04]">
            {/* Short fields always visible — auto-fit collapses to one column in
                narrow panes so long IDs/JSON wrap inside their cell, not into the
                neighbor */}
            {shortFields.length > 0 && (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-x-4 gap-y-1 text-xs mb-1">
                {shortFields.map(({ key, value }) => (
                  <div key={key} className="min-w-0">
                    <span className="text-glass-muted">{formatCustomKey(key)}: </span>
                    <span className="text-glass-secondary font-medium break-words [overflow-wrap:anywhere]">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Long fields — collapsible */}
            {longFields.length > 0 && (
              <>
                {expanded && (
                  <div className="mt-2 space-y-2.5 text-xs">
                    {longFields.map(({ key, value }) => (
                      <div key={key} className="min-w-0">
                        <p className="text-glass-muted font-medium mb-0.5">
                          {formatCustomKey(key)}
                        </p>
                        <p className="text-glass-secondary whitespace-pre-wrap break-words leading-relaxed">
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-ds-accent hover:text-ds-accent/80 anim-tap font-medium mt-1.5"
                >
                  {expanded
                    ? "See less"
                    : `See more (${longFields.length} ${longFields.length === 1 ? "field" : "fields"})`}
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
